import { MouseEvent } from "react";
import { useSelector } from "react-redux";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Button } from "@remo-co/ui-core/src/components/Button";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import {
  selectCurrentEventId,
  selectIsEventManager,
} from "modules/event/selectors";
import { selectIsLobbyBannerOpen } from "modules/lobby/redux/selectors";
import { selectIsMobile } from "modules/deviceInfo";
import { useAppDispatch } from "store/hooks";
import {
  hideShuffleAttendeesCountdownBanner,
  showShuffleAttendeesCountdownDialog,
} from "modules/shuffleAttendees/redux/actions";
import { ShuffleCountdownTimer } from "modules/shuffleAttendees/components/ShuffleCountdownTimer";
import { useShuffleAttendees } from "modules/shuffleAttendees/hooks/useShuffleAttendees";
import { useStartShuffle } from "modules/shuffleAttendees/hooks/useStartShuffle";
import { styles } from "./styles";

export const ShuffleAttendeesBanner = () => {
  const hasEventManagerAccess = useSelector(selectIsEventManager);
  const dispatch = useAppDispatch();
  const { t } = useI18n(["event"]);
  const { cancelShuffleAttendeesDoc } = useShuffleAttendees();
  const currentEventId = useSelector(selectCurrentEventId);

  const { startShuffle } = useStartShuffle();

  const showShuffleCountdownDiag = (e: MouseEvent) => {
    e.stopPropagation();
    dispatch(hideShuffleAttendeesCountdownBanner());
    dispatch(showShuffleAttendeesCountdownDialog());
  };

  const handleCountdownEnd = () => {
    dispatch(hideShuffleAttendeesCountdownBanner());
    startShuffle();
  };

  const isLobbyBannerOpen = useSelector(selectIsLobbyBannerOpen);
  const isMobileView = useSelector(selectIsMobile);

  if (isMobileView) {
    return (
      <Box sx={styles.shuffleBanner}>
        <ShuffleCountdownTimer size={30} onCountdownEnd={handleCountdownEnd} />
        <Typography variant="body1">
          {t("event:seconds.left.shuffle")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={styles.shuffleBanner} onClick={showShuffleCountdownDiag}>
      <ShuffleCountdownTimer size={40} onCountdownEnd={handleCountdownEnd} />
      <Box sx={styles.bodyText}>
        <Typography variant="body1">{t("event:shuffle.guests")}</Typography>
        {!isLobbyBannerOpen && (
          <Typography variant="caption">{t("wrap.conversation")}</Typography>
        )}
      </Box>
      {hasEventManagerAccess && !isLobbyBannerOpen ? (
        <Button
          data-testid="cancel-shuffling-btn"
          variant="warning"
          size="sm"
          onClick={() =>
            currentEventId && cancelShuffleAttendeesDoc(currentEventId)
          }
          sx={styles.button}
        >
          {t("cancel.shuffle")}
        </Button>
      ) : (
        <Button
          data-testid="shuffle-countdown-dialog"
          variant="secondary"
          color="blue"
          size="sm"
          onClick={showShuffleCountdownDiag}
          sx={styles.button}
        >
          <span>{t("event:view")}</span>
        </Button>
      )}
    </Box>
  );
};
